import { Check, Circle, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

export type SessionStage = "connect" | "discovery" | "validate" | "share";

interface SessionBreadcrumbProps {
  currentStage: SessionStage;
  completedStages: SessionStage[];
  dataDepth?: { table?: string; column?: string };
  onStageClick?: (stage: SessionStage) => void;
  /** Stages to omit entirely (e.g. steps without a screen yet). */
  hideStages?: SessionStage[];
}

const STAGES: { id: SessionStage; label: string }[] = [
  { id: "connect", label: "Connect Source" },
  { id: "discovery", label: "Discovery" },
  { id: "validate", label: "Validate" },
  { id: "share", label: "Share & Act" },
];

export function SessionBreadcrumb({
  currentStage,
  completedStages,
  dataDepth,
  onStageClick,
  hideStages,
}: SessionBreadcrumbProps) {
  const visibleStages = STAGES.filter(
    (s) => !(hideStages ?? []).includes(s.id),
  );
  return (
    <nav
      aria-label="Session progress"
      className="flex items-center gap-1 px-4 h-10 text-sm overflow-x-auto"
    >
      {visibleStages.map((stage, i) => {
        const isCurrent = stage.id === currentStage;
        const isCompleted = completedStages.includes(stage.id);
        const isClickable = isCompleted && onStageClick;

        return (
          <span
            key={stage.id}
            className="flex items-center gap-1 whitespace-nowrap"
          >
            {i > 0 && (
              <ChevronRight className="w-3 h-3 text-[var(--muted-foreground)]" />
            )}
            <button
              onClick={() => isClickable && onStageClick(stage.id)}
              disabled={!isClickable}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded transition-colors",
                isCurrent && "text-[var(--primary)] font-semibold",
                isCompleted &&
                  !isCurrent &&
                  "text-[var(--primary)] opacity-70 hover:opacity-100 cursor-pointer",
                !isCurrent &&
                  !isCompleted &&
                  "text-[var(--muted-foreground)] cursor-default",
              )}
            >
              {isCompleted && !isCurrent ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Circle
                  className={cn("w-3 h-3", isCurrent ? "fill-current" : "")}
                />
              )}
              {stage.label}
            </button>
          </span>
        );
      })}

      {/* Data depth extension */}
      {dataDepth?.table && (
        <>
          <ChevronRight className="w-3 h-3 text-[var(--muted-foreground)]" />
          <span className="text-[var(--foreground)] font-medium">
            {dataDepth.table}
          </span>
        </>
      )}
      {dataDepth?.column && (
        <>
          <ChevronRight className="w-3 h-3 text-[var(--muted-foreground)]" />
          <span className="text-[var(--muted-foreground)]">
            {dataDepth.column}
          </span>
        </>
      )}
    </nav>
  );
}
